import { Injectable } from '@angular/core';
import { ColetaService } from './coleta.service';
import { Campo, CampoCaptura, Coleta, Resposta } from '../models/coleta';
import { SessionStorageService } from './session-storage.service';
import {
  AppState,
  Fields,
  Form,
  FormItem,
  FormValueObject,
  GroupField,
} from '../models/app-state';
import { isEmpty, kebabCase, reduce, sortBy, values } from 'lodash';
import { Router } from '@angular/router';
import { PROPRIEDADES } from '../components/dynamic-inputs/util/constants';
import { AddressService } from './address.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Subject } from 'rxjs';
import { sub } from 'date-fns';

const fieldsToIgnore = [
  'captcha.resposta',
  // 'pessoa.celular.sms',
  'pessoa.assinatura',
];
const IMAGE_STORE_NAME = 'images';

@Injectable({
  providedIn: 'root',
})
export class AppFlowService {
  constructor(
    private coletaService: ColetaService,
    private addressService: AddressService,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private dbService: NgxIndexedDBService
  ) {}

  reloadComponent: Subject<{ image: { url: string; type: string } }[]> = new Subject<{ image: { url: string; type: string } }[]>();

  fieldsChangedObservable: Subject<string> = new Subject<string>();

  formatFieldKey(item: Campo | Coleta): string {
    return [item.id, kebabCase(item.titulo)].join('-');
  }

  sortByOrdem(campo: Campo): any {
    const order = campo?.atributos?.find(
      ({ propriedade }) => propriedade === PROPRIEDADES.ORDEM
    );
    return Number(order?.valor);
  }

  async getImageBase64(codigoId: string): Promise<CampoCaptura> {
    return new Promise((resolve) => {
      this.dbService
        .getByID(IMAGE_STORE_NAME, codigoId)
        .subscribe((item: any) => {
          resolve(item);
        });
    });
  }

  async getAllImages(): Promise<any> {
    return this.dbService.getAll(IMAGE_STORE_NAME).toPromise();
  }

  processGroups(groups: Coleta[], pages: string[], groupName?: string): Fields {

    let fields: Fields = {};
    // console.log(groups);
    // console.log(pages);
    // console.log(groupName);


    sortBy(groups, 'ordem').forEach((group: Coleta) => {

      if (group.campos && group.campos.length > 0) {
        const campos: Campo[] = group.campos.filter(
          (item) => item.codigo && fieldsToIgnore.indexOf(item.codigo) === -1
        );
        if (campos.length > 0) {
          const groupFields: Fields[] = sortBy(
            campos,
            this.sortByOrdem
          ) as Fields[];

          let pageName = this.formatFieldKey(group);

          // const isTOS = campos.some(
          //   (item) => item && item.codigo === 'pessoa.termoAceite'
          // );
          // const isBiometria =
          //   campos.some((item) => item && item.tipo === 'CAPTURA_IMAGEM') &&
          //   pages.indexOf('biometria') === -1;

          // if (isBiometria) {
          //   pages.push('biometria');
          // }

          pages.push(pageName);

          fields[pageName] = {
            fields: groupFields,
            title: group.titulo,
            group: groupName,
            description: group.descricao,
          };
        }
      } else if (group.grupos.length > 0) {
        fields = {
          ...fields,
          ...this.processGroups(
            sortBy(group.grupos, this.sortByOrdem) as Coleta[],
            pages,
            group?.titulo
          ),
        };
      }
    });
    return fields;
  }

  processSingleGroup(group: Coleta, pages: String[]): Fields {

    let fields: Fields = {};

    if(group.grupos) return fields;

    const campos: Campo[] = group.campos.filter(
      (item) => item.codigo && fieldsToIgnore.indexOf(item.codigo) === -1
    );

    const groupFields: Fields[] = sortBy(
      campos,
      this.sortByOrdem
    ) as Fields[];

    const pageName = this.formatFieldKey(group);

    fields[pageName] = {
      fields: groupFields,
      title: group.titulo,
      group: group.titulo,
      description: group.descricao,
    };

    pages.push(pageName);

    return fields;
  }

  computeFields(fields: Fields): Record<string, Campo> {
    let allFields: Campo[] = [];
    const keys = Object.keys(fields);
    keys.forEach((el) => {
      allFields = [...allFields, ...fields[el].fields];
    });

    const fieldsAsObject: Record<string, any> = allFields.reduce(
      (previousValue, currentValue) => ({
        ...previousValue,
        // @ts-ignore
        [currentValue.codigo]: currentValue,
      }),
      {}
    );

    return fieldsAsObject;
  }

  async startApp(coletaParam?: Coleta[]): Promise<AppState> {
    // const [coleta]: Coleta[] = await this.coletaService.get();

    const coletaFull: Coleta[] = coletaParam ? coletaParam : await this.coletaService.get();

    // console.log(coletaFull);
    const pages: string[] = [];
    let fields: Fields = {};

    coletaFull.forEach(coleta =>
      {
        Object.assign(fields, this.processGroups(coleta.grupos, pages, coleta?.titulo));
      }
      );

    // console.log(fields);


    // if(coleta.grupos){
    //   coleta.campos?.forEach((item) => {
    //     fields[this.formatFieldKey(item)] = {
    //       fields: [item],
    //       title: item.tipo ?? '',
    //       description: item.descricao ?? '',
    //     };
    //   });
    // }

    fields = {
      ...fields,
      // ...this.processGroups(coleta.grupos, pages, coleta?.titulo),
      ...this.processSingleGroup(coletaFull[0], pages)
    };

    const fieldsAsObject = this.computeFields(fields);

    let fieldsWithoutResponses: Fields = JSON.parse(JSON.stringify(fields));

    const keys = Object.keys(fieldsWithoutResponses);
    keys.forEach((el) => {
      fieldsWithoutResponses[el].fields.forEach(f => delete f.respostas)
    });

    const appState: AppState = {
      pages,
      fields: fieldsWithoutResponses,
      fieldsAsObject,
      coleta: [],
      form: {},
      stepTotal: pages.length,
      currentStepIndex: 0,
      lastStepIndex: 0,
      field: fieldsWithoutResponses[pages[0]]
    };

    return this.sessionStorageService.saveAppState(appState);
  }

  getAppState(): AppState {
    return this.sessionStorageService.getAppState();
  }

  getFormValues(): any {
    const appState = this.getAppState();
    return reduce(
      appState.form,
      (acc, value: FormItem) => {
        const formValues: any = values(value.value).reduce(
          (accumulator, { campoCodigo, valor }) => {
            if (!valor) {
              return accumulator;
            }
            return {
              ...accumulator,
              [campoCodigo]: valor,
            };
          },
          {}
        );
        return { ...acc, ...formValues };
      },
      {}
    );
  }

  getCurrentStepName(): string {
    const appState = this.getAppState();
    return appState.pages[appState.currentStepIndex];
  }

  getStepIndexByName(stepName: string): number {
    const appState: AppState = this.getAppState();
    return appState.pages.indexOf(kebabCase(stepName));
  }

  changeStepByName(stepName: string): void {
    if (!stepName) {
      return;
    }
    const appState: AppState = this.getAppState();
    const nextStepIndex = this.getStepIndexByName(stepName);
    const nextState: AppState = {
      ...appState,
      field: appState.fields[stepName],
      currentStepIndex: nextStepIndex,
    };
    this.sessionStorageService.saveAppState(nextState);
  }

  async handleArrowStepButtons(nextStepIndex: number): Promise<void> {
    const appState: AppState = this.getAppState();
    const stepName = appState.pages[nextStepIndex];
    this.changeStepByName(stepName);
    await this.router.navigate([stepName]);
  }

  async nextStep(ignoreValidation = false): Promise<void> {
    const appState: AppState = this.getAppState();
    const nextStepIndex =
      appState.currentStepIndex > -1
        ? appState.currentStepIndex + 1
        : appState.currentStepIndex;
    if (ignoreValidation || nextStepIndex < appState.lastStepIndex) {
      await this.handleArrowStepButtons(nextStepIndex);
    }
  }

  async prevStep(): Promise<void> {
    const appState: AppState = this.getAppState();
    if (appState.currentStepIndex && appState.currentStepIndex > 0) {
      const currentStepIndex = appState.currentStepIndex - 1;
      await this.handleArrowStepButtons(currentStepIndex);
    }
  }

  _reducerFormValueToField(form: any, field: GroupField): FormValueObject {
    return reduce(
      form,
      (acc, value: string, key: string) => {
        const fieldValue = field?.fields.find((fieldItem) => {
          return fieldItem.codigo === key;
        });
        if (!fieldValue) {
          return {
            ...acc,
            [key]: {
              valor: value,
              campoCodigo: key,
              campoId: -1,
            },
          };
        }
        return {
          ...acc,
          [key]: {
            campoCodigo: fieldValue.codigo,
            campoId: fieldValue.id,
            valor: value,
            ordem: fieldValue.atributos?.find(
              (item) => item.propriedade === 'ORDEM'
            )?.valor,
            oculto:
              fieldValue.atributos?.find(
                (item) => item.propriedade === 'OCULTO'
              )?.valor === 'true' ?? false,
            tipo: fieldValue.tipo,
            titulo: fieldValue.titulo,
          },
        };
      },
      {}
    );
  }

  async _cepFormValue(cep: string): Promise<Form> {
    const appState: AppState = this.sessionStorageService.getAppState();
    const addressFormValue = await this.addressService.fetchCep(cep);
    const nextForm: Form = {};

    if (addressFormValue.tipoLogradouro && addressFormValue.logradouro) {
      addressFormValue.logradouro = `${addressFormValue.tipoLogradouro} ${addressFormValue.logradouro}`;
    }

    // tslint:disable-next-line:forin
    for (const pageName in appState.fields) {
      const itemField: GroupField = appState.fields[pageName];
      let addressValue = {};
      itemField.fields.forEach((item: Campo) => {
        addressValue = reduce(
          addressFormValue,
          (acc, address, itemKey) => {
            const addressKey = itemKey === 'municipio' ? 'cidade' : itemKey;
            if (item.codigo && item.codigo.indexOf(addressKey) > -1) {
              return { ...acc, [item.codigo]: address };
            }
            return { ...acc };
          },
          addressValue
        );
      });
      if (!isEmpty(addressValue)) {
        nextForm[pageName] = {
          title: itemField.title,
          description: itemField.description,
          value: this._reducerFormValueToField(addressValue, itemField),
        };
      }
    }
    return nextForm;
  }

  async changeStep(form: any): Promise<string | null> {
    const appState: AppState = this.sessionStorageService.getAppState();
    const currentPageName = appState.pages[appState.currentStepIndex];
    let nextForm: Form = {
      ...appState.form,
    };
    // try {
    //   for (const key in form) {
    //     if (key.indexOf('cep') > -1) {
    //       nextForm = {
    //         ...nextForm,
    //         ...(await this._cepFormValue(form[key])),
    //       };
    //     }
    //   }
    // } catch (e) {
    //   console.warn('CEP error', e);
    // }

    nextForm[currentPageName] = {
      description: appState?.field?.description,
      title: appState?.field?.title,
      value: this._reducerFormValueToField(form, appState.field),
      group: appState?.field?.group,
    };

    const totalPages = appState.pages.length;
    const nextStep =
      totalPages > appState.currentStepIndex
        ? appState.currentStepIndex + 1
        : totalPages;
    const lastStep =
      nextStep > appState.lastStepIndex ? nextStep : appState.lastStepIndex;
    let nextPageName = appState.pages[nextStep];

    if (nextStep === totalPages) {
      nextPageName = 'revisar';
    }

    const field: GroupField = appState.fields[nextPageName];

    if(nextForm){
      const nextState: AppState = {
        ...appState,
        form: nextForm,
        field,
        lastStepIndex: lastStep,
        currentStepIndex: nextStep,
      };

      this.sessionStorageService.saveAppState(nextState);
    }

    return nextPageName;
  }

  // getCampoByCodigo(codigo: string) {
  //   const state = this.getAppState();
  //   //COLETAARRAY
  //   let campo =
  //     state.coleta[0].campos.find((item) => item.codigo === codigo) || {};
  //   return campo;
  // }

  async clearAppState(): Promise<void> {
    this.sessionStorageService.clearAppState();
    await this.dbService.clear(IMAGE_STORE_NAME).toPromise();
  }

  getCamposFromAppState(grupos: Coleta[]): Campo[]{
    const campos: Campo[] = 
    grupos.reduce(
      (acc, coletaAtual) => {
        if(coletaAtual.campos){
          Object.assign(acc, coletaAtual.campos);
        }

        if(coletaAtual.grupos){
          const subCampos: Campo[] = this.getCamposFromAppState(coletaAtual.grupos);
          // = grupos.reduce((acc, grupo) => Object.assign(acc, grupo.campos.map(r => r.respostas)), []);

          Object.assign(acc, subCampos);
        }

      return acc;
      }, []);

    return campos;
      
  }

  invertDateFormat(dateString: string): string {
    const parts = dateString.split('-');
    
    const invertedDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
  
    return invertedDate;
  }

  getRespostasByCampo(campo: string){
    const allFields = this.getAppState().fieldsAsObject;

    if (allFields && allFields[campo]?.tipo === 'DATA') {
      const respostas: Resposta[] | undefined = allFields[campo].respostas?.map(resposta => {
        resposta.valor = this.invertDateFormat(resposta.valor);
        return resposta;
      }) || undefined;

      return respostas;
    }
    
    const respostas: Resposta[] | undefined = allFields ? allFields[campo]?.respostas : undefined;
    return respostas;
  }

  getCampoByCodigo(campo?: string): Campo | undefined{

    if(!campo) return;
    
    const allFields = this.getAppState().fieldsAsObject;

    const campoReturn: Campo | undefined = allFields[campo] || undefined;
    return campoReturn;
  }


}
